import { Autocomplete, Group, Select, Stack, Switch, Text } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ScheduleValue {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  dayOfWeek: number; // 0-6 (0=Sun)
  dayOfMonth: number; // 1-31
  hour: number; // 0-23
  minute: number; // 0-59
}

interface ScheduleSelectorProps {
  value: ScheduleValue;
  onChange: (value: ScheduleValue) => void;
}

const DAYS_OF_WEEK_KEYS = [
  'create_v2.website.schedule.days.0', // 日曜日 (Sun)
  'create_v2.website.schedule.days.1', // 月曜日 (Mon)
  'create_v2.website.schedule.days.2', // 火曜日 (Tue)
  'create_v2.website.schedule.days.3', // 水曜日 (Wed)
  'create_v2.website.schedule.days.4', // 木曜日 (Thu)
  'create_v2.website.schedule.days.5', // 金曜日 (Fri)
  'create_v2.website.schedule.days.6', // 土曜日 (Sat)
];

export const ScheduleSelector = ({ value, onChange }: ScheduleSelectorProps) => {
  const { t } = useTranslation();
  const update = (partial: Partial<ScheduleValue>) => {
    onChange({ ...value, ...partial });
  };

  const frequencyData = [
    { value: 'daily', label: t('create_v2.website.schedule.daily') },
    { value: 'weekly', label: t('create_v2.website.schedule.weekly') },
    { value: 'monthly', label: t('create_v2.website.schedule.monthly') },
  ];

  const dayOfWeekData = DAYS_OF_WEEK_KEYS.map((key, index) => ({
    value: String(index),
    label: t(key),
  }));

  const dayOfMonthData = Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1),
    label: t('create_v2.website.schedule.day_format', { day: i + 1 }),
  }));

  // 15 分钟间隔的时间选项
  const timeOptions = Array.from({ length: 96 }, (_, i) => {
    const h = Math.floor(i / 4);
    const m = (i % 4) * 15;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  });

  const formatTime = (h: number, m: number) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

  const [timeInput, setTimeInput] = useState(formatTime(value.hour, value.minute));

  const handleTimeChange = (val: string) => {
    setTimeInput(val);
    // 解析 HH:MM 格式
    const match = val.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      const h = Number(match[1]);
      const m = Number(match[2]);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        update({ hour: h, minute: m });
      }
    }
  };

  return (
    <div className="p-4 bg-gray-50 rounded-lg border">
      <Stack gap="xs">
        <Group justify="space-between">
          <Text size="sm" fw={500}>
            {t('create_v2.website.refresh_frequency.title')}
          </Text>
          <Switch checked={value.enabled} onChange={(e) => update({ enabled: e.currentTarget.checked })} size="md" />
        </Group>

        <Group gap="xs" wrap="nowrap">
          <Select
            disabled={!value.enabled}
            size="sm"
            value={value.frequency}
            onChange={(v) => update({ frequency: v as ScheduleValue['frequency'] })}
            data={frequencyData}
            allowDeselect={false}
            style={{ minWidth: '100px' }}
          />

          {value.frequency === 'weekly' && (
            <Select
              disabled={!value.enabled}
              size="sm"
              value={String(value.dayOfWeek)}
              onChange={(v) => update({ dayOfWeek: Number(v) })}
              data={dayOfWeekData}
              allowDeselect={false}
              style={{ minWidth: '100px' }}
            />
          )}

          {value.frequency === 'monthly' && (
            <Select
              disabled={!value.enabled}
              size="sm"
              value={String(value.dayOfMonth)}
              onChange={(v) => update({ dayOfMonth: Number(v) })}
              data={dayOfMonthData}
              allowDeselect={false}
              style={{ minWidth: '80px' }}
            />
          )}

          <Autocomplete
            disabled={!value.enabled}
            size="sm"
            value={timeInput}
            onChange={handleTimeChange}
            data={timeOptions}
            filter={({ options }) => options}
            maxDropdownHeight={200}
            style={{ width: '90px' }}
          />
        </Group>

        <Text size="xs" c="gray.6">
          {t('create_v2.website.schedule.description')}
        </Text>
      </Stack>
    </div>
  );
};
